/**
 * AbstractUSBSigner - Abstract class for USB Token signing
 *
 * USB Token là loại ký số sử dụng thiết bị USB cắm trực tiếp vào máy tính.
 * Mỗi nhà cung cấp (Viettel, VNPT, etc.) có plugin riêng chạy trên localhost.
 */

import { BaseSignatureProvider } from './ISignatureProvider';
import {
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  CertificateInfo,
} from '../types';

export interface USBPluginConfig {
  /** URL của plugin (thường là localhost) */
  pluginUrl: string;
  /** Port của plugin */
  port: number;
  /** Timeout cho các request (ms) */
  timeout?: number;
}

/**
 * Abstract USB Signer
 * Các concrete class (ViettelUSBSigner, VNPTUSBSigner, etc.) sẽ extend class này
 */
export abstract class AbstractUSBSigner extends BaseSignatureProvider {
  readonly signType = SignType.USB;
  abstract readonly signProvider: SignProvider;
  abstract readonly displayName: string;

  protected config: USBPluginConfig;
  protected sessionId: string = '';

  constructor(config?: Partial<USBPluginConfig>) {
    super();
    this.config = {
      pluginUrl: config?.pluginUrl || 'http://127.0.0.1',
      port: config?.port || 14007,
      timeout: config?.timeout || 30000,
    };
  }

  /**
   * Lấy base URL của plugin
   */
  protected get baseUrl(): string {
    return `${this.config.pluginUrl}:${this.config.port}`;
  }

  /**
   * Khởi tạo session với plugin
   * Mỗi provider có cách khởi tạo khác nhau
   */
  abstract initialize(): Promise<boolean>;

  /**
   * Lấy danh sách certificate từ USB token
   * Mỗi provider có API khác nhau
   */
  abstract getCertificateInfo(): Promise<CertificateInfo | null>;

  /**
   * Ký PDF bằng USB token
   */
  abstract signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult>;

  /**
   * Ký file bằng USB token
   */
  abstract signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult>;

  /**
   * Ký XML bằng USB token
   */
  abstract signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult>;

  /**
   * Kiểm tra plugin đang chạy
   */
  async checkPluginHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Hiển thị lỗi cho user
   */
  protected showPluginNotRunningError(): void {
    console.error(`Plugin ${this.displayName} chưa chạy tại ${this.baseUrl}`);
  }

  dispose(): void {
    this.sessionId = '';
    super.dispose();
  }
}
